import { useNavigate } from "react-router";

import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import useTheme from "@mui/material/styles/useTheme";

import { useAppDispatch } from "app/hooks";
import { neutral } from "app/themes/colors";
import { APPOINTMENT_STATUSES } from "constants/appointments";
import { VIEW_OWN_SCHEDULED_ONLY } from "constants/permissions";
import { usePatientDrawer } from "features/patients/patient-drawer/use-patient-drawer.hook";
import useAppointments from "features/scheduling/hooks/use-appointments.hook";
import { openForm } from "features/scheduling/scheduler-slice";
import useUserRoles from "hooks/use-user-roles.hook";
import { Appointment } from "services/types/appointments.type";
import { Patient } from "services/types/patients.type";
import { formatBackendDateOnly } from "utils/common";

import TextCaption from "./text-caption.component";

const cardSx = {
  border: `1px solid ${neutral[100]}`,
  borderRadius: "8px",
  lineHeight: 1.6,
  background: neutral[50]
};

function AppointmentCard({ appointment }: { appointment: Appointment }) {
  const theme = useTheme();

  return (
    <Stack gap={0.5} p={1.5} boxShadow={theme.shadows[3]} sx={cardSx}>
      <Typography variant="body2" fontWeight="fontWeightBold">
        {appointment.appointmentType?.name || appointment.title}
      </Typography>
      <Typography variant="body2">
        <TextCaption>Date:</TextCaption>{" "}
        {appointment.startDate
          ? formatBackendDateOnly(appointment.startDate, "MMM dd, yyyy")
          : "-"}
      </Typography>
      <Typography variant="body2">
        <TextCaption>Provider:</TextCaption>{" "}
        {appointment.staffMember
          ? `${appointment.staffMember.first_name} ${appointment.staffMember.last_name}`
          : "-"}
      </Typography>
      <Typography variant="body2">
        <TextCaption>Clinic:</TextCaption> {appointment.clinic?.name || "-"}
      </Typography>
      <Typography variant="body2">
        <TextCaption>Status:</TextCaption>{" "}
        {APPOINTMENT_STATUSES.getFromCode(appointment.status) || "-"}
      </Typography>
    </Stack>
  );
}

export function PatientAppointments({ patient }: { patient: Patient }) {
  const theme = useTheme();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { userPermissions, currentUser: staffMember } = useUserRoles();
  const { setupAppointmentModal, appointmentFormId } = useAppointments();
  const viewOwnScheduleOnly = userPermissions[VIEW_OWN_SCHEDULED_ONLY];

  const { upcomingAppointment, pastAppointment } = usePatientDrawer();

  return (
    <Stack gap={2}>
      <Stack gap={1}>
        <Typography variant="body3" fontWeight="700">
          Upcoming Appointment
        </Typography>
        {upcomingAppointment ? (
          <AppointmentCard appointment={upcomingAppointment} />
        ) : (
          <Stack boxShadow={theme.shadows[3]} sx={cardSx}>
            <Typography color={grey[700]} textAlign="center" my={1}>
              No Upcoming Appointments
            </Typography>

            <Button
              onClick={() => {
                setupAppointmentModal(
                  {
                    patient,
                    staffMember: viewOwnScheduleOnly ? staffMember : undefined,
                    staffMemberId: viewOwnScheduleOnly
                      ? staffMember?.id
                      : undefined
                  },
                  "add",
                  () => navigate(`/patients/${patient.id}/appointments`)
                );
                dispatch(openForm(appointmentFormId));
              }}
            >
              Schedule now
            </Button>
          </Stack>
        )}
      </Stack>

      <Stack gap={1}>
        <Typography variant="body3" fontWeight="700">
          Past Appointment
        </Typography>
        {pastAppointment ? (
          <AppointmentCard appointment={pastAppointment} />
        ) : (
          <Stack boxShadow={theme.shadows[3]} sx={cardSx}>
            <Typography color={grey[700]} textAlign="center" my={1}>
              No Past Appointments
            </Typography>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
